/**
 * Validates an email address format
 * @param {string} email - The email to validate
 * @returns {boolean} - Whether the email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates password strength
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with isValid flag and message
 */
export const validatePassword = (password) => {
  if (!password || password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long'
    };
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
    return {
      isValid: false,
      message: 'Password must include uppercase, lowercase, numbers, and special characters'
    };
  }

  return {
    isValid: true,
    message: 'Password is strong'
  };
};

/**
* Validates password strength
* @param {string} password - The password to validate
* @returns {boolean} - Whether the password is valid
*/
export const isValidPassword = (password) => {
  const uppercasePattern = /[A-Z]/;
  const lowercasePattern = /[a-z]/;
  const numberPattern = /[0-9]/;
  const specialCharacterPattern = /[^A-Za-z0-9]/;
  const passwordLength = password.length >= 8;

  return passwordLength &&
    uppercasePattern.test(password) &&
    lowercasePattern.test(password) &&
    numberPattern.test(password) &&
    specialCharacterPattern.test(password);
};

/**
 * Validates secret phrases
 * @param {Array<string>} phrases - Array of phrases to validate
 * @returns {Object} - Validation result with isValid flag and message
 */
export const validatePhrases = (phrases) => {
  if (!phrases || !Array.isArray(phrases)) {
    return {
      isValid: false,
      message: 'Invalid phrases format'
    };
  }

  const emptyPhrases = phrases.filter(phrase => !phrase || !phrase.trim());

  if (emptyPhrases.length > 0) {
    return {
      isValid: false,
      message: `${emptyPhrases.length} phrase(s) are empty`
    };
  }

  return {
    isValid: true,
    message: 'All phrases are valid'
  };
};

/**
 * Validate the login form
 * @param {Object} formData - The form data to validate
 * @param {boolean} usePhrases - Whether to validate phrases instead of email/password
 * @returns {Object} - Validation result with isValid flag and message
 */
export const validateLoginForm = (formData, usePhrases = false) => {
  if (usePhrases) {
    return validatePhrases(formData.phrases);
  } else {
    const emailValidation = isValidEmail(formData.email);
    if (!emailValidation) {
      return {
        isValid: false,
        message: 'Please enter a valid email address'
      };
    }

    if (!formData.password) {
      return {
        isValid: false,
        message: 'Password is required'
      };
    }

    return {
      isValid: true,
      message: 'Form is valid'
    };
  }
};
